import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import Segment from 'helpers/segment';
import { fetchEntity } from '@classpass/saga-tools';
import { INVENTORY_ENTITY_TYPE } from 'constants/logic';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { transformKeysToCamelCase, transformKeysToSnakeCase } from 'helpers/core';
import { trackUnsupportedSchedulingPlatformSelection } from 'helpers/events';
import { reportError } from 'helpers/error-logging';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import { forceRedirectOutsideReact } from 'helpers/page-utils';
import { selectRouteParams, selectIaLogic, selectVenueByVenueId, selectActiveVenueId } from 'selectors/general';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { closeModal, waitForModal } from 'components/Modal';
import URLS from 'constants/urls';
import {
  instructorCalendars as instructorCalendarsEntity,
  calendarConfigurationPlatforms as calendarConfigurationPlatformsEntity,
  userInfo as userInfoEntity,
  googleCalendars as googleCalendarsEntity,
  services as servicesEntity,
  staffAndEquipment as staffAndEquipmentEntity,
  practitioners as practitionersEntity,
  resources as resourcesEntity,
} from 'store/saga/entities/bound';
import { ACTIVE_VENUE_CHANGED } from 'features/App/App.saga';
import messages from './CalendarLinks.messages';
import { messages as calSyncMessages } from './Components/CalSyncPractitionersList/CalSyncPractitionersList.messages';
import { flowTracker, TRACKING_IDS } from './tracking';
import { selectBookableTypeAndId } from './CalendarLinks.selectors';
import { BOOKABLE_TYPES, CALENDAR_SOURCES, ENTITY_TYPES } from './CalSync.helpers';

export const GENERATE_RESOURCE_ICAL_EXPORT_KEY = 'GENERATE_RESOURCE_ICAL_EXPORT_KEY';
export const generateResourceIcalExportKey = resourceId => ({
  type: GENERATE_RESOURCE_ICAL_EXPORT_KEY,
  resourceId,
});

export const GENERATE_VENUE_ICAL_EXPORT_KEY = 'GENERATE_VENUE_ICAL_EXPORT_KEY';
export const generateVenueIcalExportKey = () => ({
  type: GENERATE_VENUE_ICAL_EXPORT_KEY,
});

export const SHOW_ICAL_EXPORT_URL_MODAL = 'SHOW_ICAL_EXPORT_URL_MODAL';
export const showIcalExportModal = exportUrl => ({
  type: SHOW_ICAL_EXPORT_URL_MODAL,
  exportUrl,
});

export const UPDATE_INSTRUCTOR_CALENDARS = 'UPDATE_INSTRUCTOR_CALENDARS';
export const updateInstructorCalendars = (formValues, formAsyncFns) => ({
  type: UPDATE_INSTRUCTOR_CALENDARS,
  formValues,
  formAsyncFns,
});

export const DELETE_INSTRUCTOR_CALENDAR = 'DELETE_INSTRUCTOR_CALENDAR';
export const deleteInstructorCalendar = (instructorId, source) => ({
  type: DELETE_INSTRUCTOR_CALENDAR,
  instructorId,
  source,
});

export const UPDATE_VENUE_SCHEDULING_PLATFORM = 'UPDATE_VENUE_SCHEDULING_PLATFORM';
export const updateVenueSchedulingPlatform = (formValues, formAsyncFns) => ({
  type: UPDATE_VENUE_SCHEDULING_PLATFORM,
  formValues,
  formAsyncFns,
});

export const RESTART_CALENDAR_SYNC_INTEGRATION = 'RESTART_CALENDAR_SYNC_INTEGRATION';
export const restartCalendarSyncIntegration = () => ({ type: RESTART_CALENDAR_SYNC_INTEGRATION });

export const CHANGE_GOOGLE_ACCOUNT = 'CHANGE_GOOGLE_ACCOUNT';
export const changeGoogleAccount = () => ({ type: CHANGE_GOOGLE_ACCOUNT });

export const UPDATE_PRACTITIONER = 'UPDATE_PRACTITIONER';
export const updatePractitioner = (formValues, formAsyncFns) => ({
  type: UPDATE_PRACTITIONER,
  formValues,
  formAsyncFns,
});

const FETCH_BOOKABLE = 'FETCH_BOOKABLE';
export const fetchBookable = staffId => ({
  type: FETCH_BOOKABLE,
  staffId,
});

export function* doUpdateInstructorCalendars(client, action) {
  const { venueId } = yield select(selectRouteParams);
  const { formValues, formAsyncFns } = action;
  const { source } = formValues;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;
  const { httpClient } = client;
  const endpoint = URLS.instructorCalendars.update(venueId);
  const stepTracker = flowTracker.step(TRACKING_IDS.CALENDAR_SAVED);
  braze.trackEvent(BRAZE_CUSTOM_EVENTS.CALSYNC_INTEGRATION_SYNC);

  try {
    const {
      data: { success, error },
    } = yield call([httpClient, httpClient.post], endpoint, transformKeysToSnakeCase(formValues));

    if (!success) {
      yield put(createErrorBanner(messages.sagaError));
      yield call(rejectSubmit, messages.sagaError);
      yield call(stepTracker.error, { error, source });
      return;
    }

    const newPath = makePath.calendarLinks(venueId);
    yield all([
      call(resolveSubmit, messages.sagaSaveSuccess),
      call(client.navigate, newPath),
      call(stepTracker.complete, { source }),
    ]);
  } catch (error) {
    yield call(rejectSubmit, messages.sagaError);
    yield call(stepTracker.error, { error, source });
  }
}

export function* doDeleteInstructorCalendar(client, action) {
  const { venueId } = yield select(selectRouteParams);
  const { instructorId, source } = action;
  const { httpClient } = client;
  const endpoint = URLS.instructorCalendars.delete(venueId);
  const stepTracker = flowTracker.step(TRACKING_IDS.CALENDAR_REMOVED);

  const confirmed = yield call(waitForModal, client, 'DeleteCalendarConfirmationModal');

  if (!confirmed) {
    return;
  }

  try {
    const {
      data: { success, error },
    } = yield call([httpClient, httpClient.post], endpoint, {
      source,
      entity_id: instructorId,
      entity_type: INVENTORY_ENTITY_TYPE.TEACHER,
    });

    if (!success) {
      yield put(createErrorBanner(messages.sagaError));
      yield call(stepTracker.error, { error, venueId, calendarType: source });
      return;
    }

    const newPath = makePath.calendarLinks(venueId);
    yield all([
      put(createInfoBanner(messages.sagaDeleteSuccess)),
      call(client.navigate, newPath),
      call(stepTracker.complete, { venueId, calendarType: source }),
    ]);
  } catch (error) {
    yield put(createErrorBanner(messages.sagaError));
    yield call(stepTracker.error, { error, venueId, calendarType: source });
  }
}

export function* doUpdateVenueSchedulingPlatform(client, action) {
  try {
    const { venueId } = yield select(selectRouteParams);
    const { formValues } = action;
    const { schedulingPlatform } = formValues;
    const { value, source } = schedulingPlatform;
    const { httpClient } = client;

    if (source === CALENDAR_SOURCES.ICAL) {
      const endpoint = URLS.venue.calendarConfiguration(venueId);
      yield call([httpClient, httpClient.post], endpoint, { platform: value });
      yield call(fetchEntity, userInfoEntity, [client]);
    } else if (source === CALENDAR_SOURCES.NOT_LISTED) {
      const modalResp = yield call(waitForModal, client, 'NotListedPlatformModal');

      if (!modalResp) {
        yield put(closeModal());
        return;
      }

      const { platformName } = modalResp;
      yield all([
        put(closeModal()),
        call(trackUnsupportedSchedulingPlatformSelection, { platformName }),
        call(client.navigate, makePath.appointmentsCalendar({ venueId })),
        put(createInfoBanner(messages.sagaReportPlatformNameSuccess)),
      ]);
    } else if (source === CALENDAR_SOURCES.GCAL) {
      const endpoint = URLS.venue.calendarConfiguration(venueId);
      yield call([httpClient, httpClient.post], endpoint, { platform: value });

      const googleAuthPath = URLS.venue.googleAuth(venueId);
      yield call(forceRedirectOutsideReact, googleAuthPath);
    } else {
      yield call(trackUnsupportedSchedulingPlatformSelection, { platformName: value });
      yield call(waitForModal, client, 'PlatformNotSupportedModal');
      yield call(client.navigate, makePath.appointmentsCalendar({ venueId }));
    }
  } catch (error) {
    yield put(createErrorBanner(messages.sagaError));
  }
}

export function* doRestartCalendarSyncIntegration(client) {
  const { httpClient } = client;
  const { venueId } = yield select(selectRouteParams);

  const confirmed = yield call(waitForModal, client, 'ResetCalendarSyncFlowModal');
  if (!confirmed) return;

  const endpoint = URLS.venue.googleDeleteAuth(venueId);
  yield call([httpClient, httpClient.delete], endpoint);
  yield call(fetchEntity, userInfoEntity, [client]);
  yield call(client.navigate, makePath.calendarLinks(venueId));
}

export function* doChangeGoogleAccount(client) {
  const { httpClient } = client;
  const { venueId } = yield select(selectRouteParams);
  const { calendarConfigurationPlatform } = yield select(selectVenueByVenueId, venueId);

  const confirmed = yield call(waitForModal, client, 'ChangeGoogleAccountModal');
  if (!confirmed) return;

  const deleteAuthEndpoint = URLS.venue.googleDeleteAuth(venueId);
  yield call([httpClient, httpClient.delete], deleteAuthEndpoint);

  const authEndpoint = URLS.venue.calendarConfiguration(venueId);
  yield call([httpClient, httpClient.post], authEndpoint, { platform: calendarConfigurationPlatform });

  const googleAuthPath = URLS.venue.googleAuth(venueId);
  yield call(forceRedirectOutsideReact, googleAuthPath);
}

export function* handleCalendarLinksRouteEnter(client) {
  const { venueId } = yield select(selectRouteParams);
  const { featureIsSupported } = yield select(selectIaLogic, IA_KEY.APPOINTMENTS_CALENDAR_LINK, venueId);
  const { calendarConfigurationSource } = yield select(selectVenueByVenueId, venueId);

  if (!featureIsSupported) {
    yield call(client.navigate, makePath.appointmentsCalendar({ venueId }));
    return;
  }

  if (calendarConfigurationSource === CALENDAR_SOURCES.GCAL) {
    try {
      yield call(fetchEntity, googleCalendarsEntity, [client, venueId]);
    } catch (error) {
      yield call(reportError, error);
      yield put(createErrorBanner(messages.sagaError));
    }
  }

  yield all([
    call(fetchEntity, instructorCalendarsEntity, [client, venueId]),
    call(fetchEntity, calendarConfigurationPlatformsEntity, [client]),
    call(fetchEntity, servicesEntity, [client, venueId]),
  ]);
}

export function* doShowIcalExportCopyLinkModal(client, action) {
  const { exportUrl } = action;
  const confirmed = yield call(waitForModal, client, 'IcalExportModal', { exportUrl });
  if (confirmed) {
    navigator.clipboard.writeText(exportUrl);
    yield put(createSuccessSnackbar(messages.sagaCopyLinkSuccess));
  }
}

export function* doGenerateVenueIcalExportLink(client) {
  const { httpClient } = client;
  const { venueId } = yield select(selectRouteParams);

  const endpoint = URLS.generateIcalExportKey(venueId, ENTITY_TYPES.VENUE);
  try {
    const response = yield call([httpClient, httpClient.post], endpoint);
    yield call(fetchEntity, instructorCalendarsEntity, [client, venueId]);

    const exportUrl = transformKeysToCamelCase(response).data.exportInfo.url;
    yield call(doShowIcalExportCopyLinkModal, client, { exportUrl });
  } catch (error) {
    yield put(createErrorBanner(messages.sagaError));
  }
}

export function* doGenerateResourceIcalExportLink(client, action) {
  const { httpClient } = client;
  const { resourceId } = action;

  const endpoint = URLS.generateIcalExportKey(resourceId, ENTITY_TYPES.TEACHER);
  try {
    const response = yield call([httpClient, httpClient.post], endpoint);
    const { venueId } = yield select(selectRouteParams);
    yield call(fetchEntity, instructorCalendarsEntity, [client, venueId]);

    const exportUrl = transformKeysToCamelCase(response).data.exportInfo.url;
    yield call(doShowIcalExportCopyLinkModal, client, { exportUrl });
  } catch (error) {
    yield put(createErrorBanner(messages.sagaError));
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  yield call(client.navigate, makePath.calendarLinks(venueId));
}

export function* doUpdatePractitioner(client, action) {
  const { formValues, formAsyncFns } = action;
  const { rejectSubmit } = formAsyncFns;
  const venueId = yield select(selectActiveVenueId);
  const { resourceId } = formValues;
  try {
    const { bookableType, id } = yield select(selectBookableTypeAndId, resourceId);
    const entityData = { ...formValues, id };
    const entityType = bookableType === BOOKABLE_TYPES.PRACTITIONER ? practitionersEntity : resourcesEntity;
    yield call(fetchEntity, entityType, [client, venueId, entityData]);
    yield all([
      call(Segment.event, 'CalSync Practitioner Availability Updated', { venueId, practitionerId: id }),
      call(fetchEntity, instructorCalendarsEntity, [client, venueId]),
      put(createSuccessSnackbar(calSyncMessages.staffAndEquipmentUpdated)),
    ]);
  } catch (error) {
    yield all([call(rejectSubmit), put(createErrorSnackbar(messages.sagaError))]);
  }
}

export function* doFetchBookable(client, action) {
  try {
    const { staffId } = action;
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntity, staffAndEquipmentEntity, [client, { venueId, staffId }]);
  } catch (error) {
    return null;
  }
}

export function* watchCalendarLinksActions(client) {
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(UPDATE_INSTRUCTOR_CALENDARS, doUpdateInstructorCalendars, client);
  yield takeEvery(DELETE_INSTRUCTOR_CALENDAR, doDeleteInstructorCalendar, client);
  yield takeEvery(UPDATE_VENUE_SCHEDULING_PLATFORM, doUpdateVenueSchedulingPlatform, client);
  yield takeEvery(RESTART_CALENDAR_SYNC_INTEGRATION, doRestartCalendarSyncIntegration, client);
  yield takeEvery(CHANGE_GOOGLE_ACCOUNT, doChangeGoogleAccount, client);
  yield takeEvery(GENERATE_RESOURCE_ICAL_EXPORT_KEY, doGenerateResourceIcalExportLink, client);
  yield takeEvery(GENERATE_VENUE_ICAL_EXPORT_KEY, doGenerateVenueIcalExportLink, client);
  yield takeEvery(SHOW_ICAL_EXPORT_URL_MODAL, doShowIcalExportCopyLinkModal, client);
  yield takeEvery(UPDATE_PRACTITIONER, doUpdatePractitioner, client);
  yield takeEvery(FETCH_BOOKABLE, doFetchBookable, client);
}
